<template>
	<section class="page page-place-checkins" v-bind:class="{ 'has-scrolled': hasScrolled }" v-bind:key="venueModel">
		<ChalkyUiPageHero
			description="Explore who has checked in recently at this venue."
			v-bind:imageUrl="imageUrl"
			v-bind:title="venueModel.getName()"
			v-if="venueModel.uniqueKey"
			v-on:click.native="Handle_OnClickHero"
		/>

		<ScenePlaceCheckins
			ref="scenePlaceCheckins"
			v-bind:venueCheckinsCollection="venueCheckinsCollection"
			v-if="venueCheckinsCollection"
			v-on:user:select="Handle_OnSelectUser"
		/>
	</section>
</template>

<script lang="ts">
	import * as Mixins from '@/Mixins';
	import ChalkySticks from '@chalkysticks/sdk';
	import PageBase from '@/Core/Page.vue';
	import { Component } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * Place checkins page controller. Owns the venue model and checkins
	 * collection, delegates presentation to ScenePlaceCheckinsIndex,
	 * and handles navigation.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Page
	 * @project ChalkySticks App
	 */
	@Component({
		mixins: [Mixins.AutoScroll, Mixins.StickyScroll],
	})
	export default class PagePlaceCheckins extends PageBase {
		/**
		 * URL of the hero image.
		 *
		 * @type string
		 */
		protected imageUrl: string = '';

		/**
		 * Configuration for sticky behavior.
		 *
		 * @type IStickyScrollConfig
		 */
		protected stickyConfig: any = {
			scrollThreshold: 100,
		};

		/**
		 * Venue model for this page.
		 *
		 * @type ChalkySticks.Model.Venue
		 */
		protected venueModel: ChalkySticks.Model.Venue = ChalkySticks.Factory.Venue.model();

		/**
		 * Collection of venue checkins.
		 *
		 * @type ChalkySticks.Collection.VenueCheckin
		 */
		protected venueCheckinsCollection!: ChalkySticks.Collection.VenueCheckin;

		/**
		 * Initialize venue model, create checkins collection, and fetch data.
		 *
		 * @return Promise<void>
		 */
		@mounted
		protected async setup(): Promise<void> {
			this.venueModel.set({
				id: this.$route.params.id,
			});

			// Duplicate checkins here
			this.venueCheckinsCollection = ChalkySticks.Factory.VenueCheckin.collection(this.venueModel);

			// !! Important !! Iterate through the 'user' relationship
			this.venueCheckinsCollection.atRelationship = ['user'];

			// Fetch venue checkins list model
			this.venueCheckinsCollection.fetch();

			// Fetch venue if not
			await this.venueModel.fetch();

			// Check for images
			this.imageUrl = this.venueModel.media.primary?.getUrlMedium() || '';

			// Redirect to 404
			if (!this.venueModel?.id) {
				this.$router.push({
					path: '/not-found',
				});
			}
		}

		/**
		 * Fetches next page of results via the Scene.
		 *
		 * @return Promise<void>
		 */
		protected async triggerLoadMore(): Promise<void> {
			await (this.$refs.scenePlaceCheckins as any).loadMore();
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle hero click. Navigate to venue detail page.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickHero(e: PointerEvent): Promise<void> {
			this.$store.dispatch('gotoVenue', this.venueModel);
		}

		/**
		 * Handle user selection. Navigate to user profile page.
		 *
		 * @param PointerEvent e
		 * @param ChalkySticks.Model.User userModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectUser(e: PointerEvent, userModel: ChalkySticks.Model.User): Promise<void> {
			this.$store.dispatch('gotoUser', userModel);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.page-place-checkins {
		.ui-pagehero figure::before {
			background: linear-gradient(34deg, rgba(165, 63, 66, 1) 18%, rgba(165, 63, 66, 0) 67%);
			content: ' ';
			height: 100%;
			pointer-events: none;
			position: absolute;
			opacity: 0.75;
			width: 100%;
		}
	}
</style>
